import React from "react";
import { useParams } from "react-router-dom";

// py-components
import { ButtonLoading, ButtonStandard, ModalBase, Text } from "@performyard/py-components";

// hooks
import { useGoalDetails, useGoalHistory, useMutateGoalDetails } from "../../../../../../api/hooks";
import useSearchQueryParams from "../../../../../../hooks/useSearchQueryParams";

// config
import Config from "../../../config";

type ArchiveGoalModalProps = {
	goalId?: string;
	goalName?: string;
	isOpen?: boolean;
	onClose?: () => void;
	onSuccess?: () => void;
};

const ArchiveGoalModal = ({
	goalId,
	goalName,
	isOpen: isOpenProp,
	onClose,
	onSuccess,
}: ArchiveGoalModalProps): React.ReactElement => {
	const { goal_id: goalIdFromParams } = useParams<{ goal_id: string }>();
	const resolvedGoalId = goalId ?? goalIdFromParams ?? "";

	const {
		queryParams: { modal },
		handleQueryChange,
	} = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const { data, refetch: refetchGoal } = useGoalDetails({ goalID: resolvedGoalId });
	const goal = data?.goal;
	const resolvedGoalName = goalName ?? goal?.name;

	const { refetch: refetchProgress } = useGoalHistory({
		goalID: resolvedGoalId,
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { refetch: refetchActivity } = useGoalHistory({
		goalID: resolvedGoalId,
		sortDirection: "desc",
		sortName: "_id",
	});

	const isOpen = typeof isOpenProp === "boolean" ? isOpenProp : modal === "archive";

	const closeModal = (): void => {
		if (onClose) {
			onClose();
			return;
		}
		handleQueryChange({ modal: undefined });
	};

	const { mutate, isPending } = useMutateGoalDetails(resolvedGoalId, {
		onSuccess: () => {
			refetchGoal();
			refetchProgress();
			refetchActivity();
			onSuccess?.();
			closeModal();
		},
	});

	const handleArchive = (): void => {
		if (!resolvedGoalId) {
			return;
		}
		mutate([{ field: "status", value: "archived" }]);
	};

	return (
		<ModalBase isOpen={isOpen} onClose={closeModal} dangerouslySetClassName="w-[432px]">
			<div className="px-6 pb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					Archive Goal?
				</Text>
				{resolvedGoalName ? (
					<Text size="paragraph-2" weight="regular">
						{resolvedGoalName}
					</Text>
				) : null}
			</div>

			<div className="px-6 pb-6 text-center">
				<Text size="paragraph-2" weight="semi-bold">
					Are you sure you want to archive this goal? Archiving a goal will remove any alignment to this goal.
				</Text>
			</div>

			<div className="flex justify-center gap-2 px-6 pb-6">
				<div className="w-fit min-w-[90px]">
					<ButtonStandard
						variant="outline"
						color="neutral"
						size="large"
						onClick={closeModal}
						disabled={isPending}
						data-testid="cancel-archive-goal"
					>
						Cancel
					</ButtonStandard>
				</div>
				<div className="w-[120px]">
					<ButtonLoading
						color="primary"
						size="large"
						loading={isPending}
						disabled={!resolvedGoalId || isPending}
						onClick={handleArchive}
						data-testid="archive-goal"
					>
						Yes, Archive
					</ButtonLoading>
				</div>
			</div>
		</ModalBase>
	);
};

export default ArchiveGoalModal;
